import { Checkbox, Select, Space, Typography } from 'antd';
import type { ScreenDiagnostic, ScreenVocabulary } from '@/api/screens';
import { errorAt } from './diagnostics';
import { LocalizedField } from './LocalizedField';
import { PageSettings } from './PageSettings';
import { PROGRESS_LABELS, type JourneyBlock, type JourneyDef, type LocalizedText } from './model';

const { Text } = Typography;

/** Drop what is empty, so a journey with no settings has no `journey` block at all. */
function tidy(block: JourneyBlock): JourneyBlock | undefined {
  const next: JourneyBlock = { ...block };
  if (next.header && Object.keys(next.header).length === 0) delete next.header;
  if (next.progress && Object.keys(next.progress).length === 0) delete next.progress;
  if (!next.pages) delete next.pages;
  if (next.sequential === undefined) delete next.sequential;
  return Object.keys(next).length > 0 ? next : undefined;
}

/** What applies to the whole journey: languages, header, progress, and whether steps go in order. */
export function JourneySettings({
  value,
  vocabulary,
  diagnostics,
  onChange,
}: {
  value: JourneyDef;
  vocabulary: ScreenVocabulary;
  diagnostics: ScreenDiagnostic[];
  onChange: (next: JourneyDef) => void;
}) {
  const journey = value.journey ?? {};
  const header = journey.header ?? {};
  const locales = value.locales ?? ['en'];
  const setJourney = (next: JourneyBlock) => {
    const tidied = tidy(next);
    const updated: JourneyDef = { ...value, journey: tidied };
    if (!tidied) delete updated.journey;
    onChange(updated);
  };
  const setHeader = (updates: Partial<NonNullable<JourneyBlock['header']>>) => {
    const next = { ...header, ...updates };
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined) delete (next as Record<string, unknown>)[k];
    }
    setJourney({ ...journey, header: next });
  };
  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }} data-testid="journey-settings">
      <div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>Languages</div>
        <Select
          mode="multiple"
          style={{ width: 320 }}
          aria-label="Languages"
          value={locales}
          status={errorAt(diagnostics, '/locales') ? 'error' : undefined}
          options={vocabulary.locales.map((l) => ({ value: l, label: l }))}
          onChange={(next: string[]) => onChange({ ...value, locales: next })}
        />
      </div>
      <Checkbox
        data-testid="journey-sequential"
        checked={journey.sequential === true}
        onChange={(e) => setJourney({ ...journey, sequential: e.target.checked ? true : undefined })}
      >
        One step at a time
      </Checkbox>
      <Text type="secondary" style={{ fontSize: 12, marginTop: -8 }}>
        The server refuses a step while an earlier required one is open. Optional steps never hold the next one back.
      </Text>
      <div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>Progress</div>
        <Select
          style={{ width: 320 }}
          aria-label="Progress style"
          data-testid="journey-progress"
          value={journey.progress?.style ?? 'COUNT'}
          status={errorAt(diagnostics, '/journey/progress') ? 'error' : undefined}
          options={vocabulary.progressStyles.map((s) => ({ value: s, label: PROGRESS_LABELS[s] ?? s }))}
          onChange={(style: string) =>
            setJourney({ ...journey, progress: style === 'COUNT' ? {} : { style } })
          }
        />
      </div>
      <LocalizedField
        label="Page heading"
        hint="Empty uses the room's own heading."
        value={header.heading}
        locales={locales}
        diagnostics={diagnostics}
        path="/journey/header/heading"
        onChange={(heading: LocalizedText | undefined) => setHeader({ heading })}
      />
      <LocalizedField
        label="Page subheading"
        hint="Empty uses the room's own line."
        value={header.subheading}
        locales={locales}
        diagnostics={diagnostics}
        path="/journey/header/subheading"
        onChange={(subheading: LocalizedText | undefined) => setHeader({ subheading })}
      />
      <Space size={16}>
        <Checkbox
          checked={header.showApplicantName !== false}
          onChange={(e) => setHeader({ showApplicantName: e.target.checked ? undefined : false })}
        >
          Show the welcome line
        </Checkbox>
        <Checkbox
          data-testid="journey-show-caseref"
          checked={header.showCaseRef !== false}
          onChange={(e) => setHeader({ showCaseRef: e.target.checked ? undefined : false })}
        >
          Show the case reference
        </Checkbox>
      </Space>
      <PageSettings
        pages={journey.pages}
        vocabulary={vocabulary}
        locales={locales}
        diagnostics={diagnostics}
        onChange={(pages) => setJourney({ ...journey, pages })}
      />
    </Space>
  );
}
